import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
  viewChild,
} from '@angular/core';

import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Guid, isNil, Nillable, valueOrDefault } from '@web/utils';
import {
  AdminShippingMethodFormData,
  AdminShippingMethodFormGroup,
  AdminShippingMethodPriceFormData,
  AdminShippingMethodPriceFormGroup,
} from '../../models/admin-shipping-method-form-data';
import { Accordion } from 'primeng/accordion';
import {
  FormControlComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
} from '@web/shared/ui';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { AdminShippingMethodFormPricePanelComponent } from '../../components/admin-shipping-method-form-price-panel/admin-shipping-method-form-price-panel.component';
import {
  SummernoteComponent,
  SummernoteImageUpload,
} from '@web/shared/external';
import { AdminShippingMethodDataSourceService } from '../../services';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-admin-shipping-method-form',
  imports: [
    ReactiveFormsModule,
    FormControlComponent,
    Button,
    PrimengButtonDirective,
    InputText,
    PrimengInputTextDirective,
    Accordion,
    AdminShippingMethodFormPricePanelComponent,
    SummernoteComponent,
  ],
  templateUrl: './admin-shipping-method-form.component.html',
  styleUrl: './admin-shipping-method-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminShippingMethodFormComponent implements OnInit {
  private readonly builder = inject(FormBuilder);
  /* prettier-ignore */
  private readonly shippingMethodDataSource = inject(AdminShippingMethodDataSourceService);

  public readonly data = input<Nillable<AdminShippingMethodFormData>>();
  public readonly submitClickEvent = output<AdminShippingMethodFormData>();

  protected readonly accordion = viewChild(Accordion);
  protected readonly form = this.buildForm();

  protected activePriceIndex = 0;

  public ngOnInit(): void {
    this.fillByData();
  }

  public handleSubmitClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  public handlePriceRemoveClick(index: number): void {
    this.form.controls.prices.removeAt(index);
    this.activePriceIndex = this.form.controls.prices.length - 1;
  }

  public handleProductAddClick(): void {
    this.form.controls.prices.push(this.buildPriceFormGroup());
    this.activePriceIndex = this.form.controls.prices.length - 1;
  }

  public getPriceFormGroups(): AdminShippingMethodPriceFormGroup[] {
    return this.form.controls.prices.controls;
  }

  protected readonly uploadFn = (
    files: FileList,
  ): Observable<SummernoteImageUpload[]> => {
    return this.shippingMethodDataSource.uploadEditorImages(Array.from(files));
  };

  private toData(): AdminShippingMethodFormData {
    const data = this.form.getRawValue();

    return new AdminShippingMethodFormData({
      name: valueOrDefault(data.name, ''),
      category: valueOrDefault(data.category, ''),
      description: valueOrDefault(data.description, ''),
      prices: data.prices.map(
        (price) =>
          new AdminShippingMethodPriceFormData({
            price: valueOrDefault(price.price, 0),
            countryId: valueOrDefault(price.countryId, ''),
            paymentMethodIds: valueOrDefault(price.paymentMethodIds, []),
          }),
      ),
    });
  }

  private buildForm(): AdminShippingMethodFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      name: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminShippingMethodFormData.NameMaxLength)]),
      category: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminShippingMethodFormData.CategoryMaxLength)]),
      description: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminShippingMethodFormData.DescriptionMaxLength)]),
      prices: this.builder.array<AdminShippingMethodPriceFormGroup>([], [Validators.required]),
    });
  }

  private fillByData(): void {
    const data = this.data();

    if (isNil(data)) {
      this.form.controls.prices.push(this.buildPriceFormGroup());

      return;
    }

    const prices = valueOrDefault(data.prices, []);

    prices.forEach((price) =>
      this.form.controls.prices.push(this.buildPriceFormGroup(price)),
    );

    this.form.patchValue(
      {
        name: data?.name,
        category: data?.category,
        description: data?.description,
        prices: data?.prices,
      },
      { emitEvent: false },
    );
  }

  private buildPriceFormGroup(
    data?: AdminShippingMethodPriceFormData,
  ): AdminShippingMethodPriceFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      price: this.builder.control<Nillable<number>>(data?.price, [Validators.required]),
      countryId: this.builder.control<Nillable<Guid>>(data?.countryId, [Validators.required]),
      paymentMethodIds: this.builder.control<Nillable<Guid[]>>(data?.paymentMethodIds, [Validators.required]),
    });
  }
}
